import _ from 'lodash'
import $ from 'jquery'
import HeaderCell from './header-cell'
import reportConstants from 'scoreboard/reports/report-constants'
import { LAST_STACK_ADDABLE_CLASS } from './table'
import SingleColumnStack from './single-column-stack'
import MultiColumnTableStack from './multi-column-table-stack'

/**
 * Represents a cell within a stack with a repeat by layout
 */
class RepeatByHeaderCell extends HeaderCell {

	constructor(stack, columnAttrsOrBreakdownData, repeatByAttributes, opts = {}) {
		super(stack, columnAttrsOrBreakdownData, opts)
		this._repeatedStackCells = opts.repeatedStackCellContainer
		this._repeatedStackCells.push(this)
		this.repeatByAttributes = repeatByAttributes
		this.style('breakdown', !_.isNil(opts.breakdownReference))
		//the position this column appear in order in the set of columns
		this.orderIndex = opts.orderIndex
		//an array that contains all the table body dom nodes belong to the table
		//column
		this._stackBodyDomNodes = []
		//array of domNodes that compose of all the node found in header rows
		//underneath this header
		this._childHeaderDomNodes = []

		//#26584 will fix this
		if (this.inBreakdownRow()) {
			this.style('is-sortable', false)
		}

		const isSubtotal = columnAttrsOrBreakdownData.subtotal
		const parentSubtotal = !!opts.parentCell && opts.parentCell.subtotal
		if (isSubtotal || parentSubtotal) {
			this.style('is-subtotal', true)
			this.subtotal = true
		}
	}

	id() {
		if (this.inBreakdownRow()) {
			let parentHeaderCell = _.get(this.options, 'parentCell', null)
			if (parentHeaderCell) {
				return `${parentHeaderCell.id()}-${this.options.cellGroupIndex}`
			}
			return `${this.options.cellGroupIndex}`
		} else {
			return super.id()
		}
	}

	bindStackBodyDomNode(td) {
		this._stackBodyDomNodes.push(td)
	}

	addDomNode(node) {
		super.addDomNode(node)

		//if this cell has a parent header, ensure the parent headers have
		//reference to their children header dom nodes
		let parentHeaderCell = _.get(this.options, 'parentCell', null)
		while (parentHeaderCell) {
			parentHeaderCell._childHeaderDomNodes.push(node)
			parentHeaderCell = _.get(parentHeaderCell.options, 'parentCell', null)
		}
	}

	/**
	 * Returns all the cells in the row that this cell belongs to
	 */
	cellRow() {
		if (!this._cellRow) {
			this._cellRow = this.tableStack.rows(this.rowIndex())
		}
		return this._cellRow
	}

	/**
	 * Returns true if this cell is in a breakdown row
	 */
	inBreakdownRow() {
		return !_.isNil(this.options.breakdownReference)
	}

	/**
	 * Returns all the dom nodes that repeat over the entire stack for the column
	 * represented by this cell
	 */
	repeatedColumnDomNodes() {
		return _.flatMap(this._repeatedStackCells, cell => cell.singleColumnDomNodes())
	}

	/**
	 * Return the dom nodes that make up the entire header row
	 */
	rowHeaderDomNodes() {
		return _.flatMap(this.cellRow(), cell => cell.domNodes())
	}

	/**
	 * Returns the dom nodes found in the table body under a single header column
	 * for this stack
	 */
	singleColumnDomNodes() {
		return [...this.domNodes(),
			...this._childHeaderDomNodes,
			...this._stackBodyDomNodes]
	}

	/**
	 * Returns the dom nodes that compose the complete side of the header cell
	 * stack.
	 *
	 * The "complete side" is the following.  Start with the header node of
	 * this row.  If this row had children header rows, then for each child
	 * header row, find the  first/last header cell of each child row, finally
	 * add the first/last body nodes of the table body cells that belong under
	 * the lowest header row
	 */
	sideDomNodes(left) {
		let childRow = this.tableStack.rows(this.rowIndex()+1)
		let index = left ? 0 : this._childHeaderDomNodes.length-1
		let repeatedSideNodes = _.flatMap(childRow[index]._repeatedStackCells,
			cell => cell.sideDomNodes(left))
		return [...this.domNodes(), ...repeatedSideNodes]
	}

	thisColumnDomNodes() {
		return [...this.domNodes(),
			...this._stackBodyDomNodes]
	}

	/**
	 * The drag avatar representing the entire row
	 */
	rowDragAvatar() {
		if (!this._rowAvatar) {
			this._rowAvatar = []
			_.map(this.cellRow(), (breakdownCell, idx) => {
				this._rowAvatar.push(new SingleColumnStack(idx, breakdownCell))
			})
		}
		return this._rowAvatar
	}

	/**
	 * The drag avatar representing the stack using a single column
	 */
	stackDragAvatar(originatingTableStack) {
		if (!this._stackAvatar) {
			if (this.position == reportConstants.REPEAT_BY_BOTTOM_POSITION || !this.inBreakdownRow()) {
				let stackNodes = _.cloneDeep(this._stackBodyDomNodes)
				//we're just copying the values that are currently in this stack
				let dataExtractor = {
					toCellContents: (datum, cell, dataRowIndex) =>  {
						const stackNode = $(stackNodes[dataRowIndex])
						const html = stackNode.find('.content').html()

						// copy classes over from original node
						const style = {}
						_.each(stackNode[0].classList, c => style[c] = true)

						// copy inline styles over from original node for
						// conditional coloring
						const coloringStyles = ['--custom-cell-color',
							'--custom-cell-font-color']
						const inlineStyle = stackNode.css(coloringStyles)

						return { html, inlineStyle, style }
					}
				}
				let stack = new SingleColumnStack(0, this, {dataExtractor})
				this._stackAvatar = [stack]
			} else {
				let {columnAttributes, repeatByAttributes, options} = originatingTableStack.dataForStackAvatar()
				let breakdownIndex = this.position == reportConstants.REPEAT_BY_TOP_POSITION ? 0 : 1
				let breakdown = repeatByAttributes.breakdowns[breakdownIndex]
				//just use label of the column being dragged, don't use set()
				//otherwise the listeners on the original table stack are lost
				breakdown.attributes.breakdownLabelsWithId = [breakdown.get('breakdownLabelsWithId')[this.orderIndex]]
				let stack = new MultiColumnTableStack(0, columnAttributes, repeatByAttributes, options)
				this._stackAvatar = [stack]
			}
		}
		return this._stackAvatar
	}

	isSameRepeatBy(cell = null) {
		return cell && this.repeatByAttributes === cell.repeatByAttributes
	}

	_allowUngroupedPeriodRangeRepeatBy(columnType) {
		return false	//no scenario allows for this
	}

	/**
	 * Returns the dom nodes in the header row composed of a single group of
	 * repeated columns that this cell is in
	 */
	rowHeaderGroupDomNodes() {
		//find all the cells that share the same parent, these compose a single
		//repeated group
		let parentCell = this.options.parentCell
		if (parentCell) {
			let cellGroup = _.filter(this.cellRow(), cell =>
				cell.options.parentCell === parentCell)
			return _.flatMap(cellGroup, cell => cell.domNodes())
		} else {
			return [this]
		}
	}

	/**
	 * Returns the index in the column collection to add another column in this layout
	 */
	addIndex(addMarkerEl) {
		if (this.firstChild && this.options.parentCell.orderIndex != 0) {
			//if we're the first column in any group except the first
			//then we're adding to the end of the current set of columns
			return this.stack.columns().length
		} else if (addMarkerEl.classList.contains(LAST_STACK_ADDABLE_CLASS) ||
			addMarkerEl.classList.contains('addable-right')) {
			// else check to see if we're adding on the last most marker or if the
			// the button is on the right side of the column, if so add to the right
			// of the index
			return this.orderIndex + 1
		} else {
			//all other scenarios uses the order index
			return this.orderIndex
		}
	}

	_sortDirection(columnModel, reportModel) {
		//only the bottom row will show the sort icon (method is overriden)
		return null
	}


	allowRepeatBy() {
		let allowed = super.allowRepeatBy()
		if (!allowed) {
			return false
		}
		//in addition to the base case, when in repeat by, allow only if we're
		//not at the maximum of 2 breakdowns
		return this.tableStack._rows.length < 3
	}
}

export default RepeatByHeaderCell
RepeatByHeaderCell.__filename__ = 'reports/common/table/repeat-by-header-cell.js'
